import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, Trash2Icon, ImageIcon, CheckCircleIcon } from 'lucide-react';
interface Option {
  id: string;
  name: string;
  values: string;
}
interface Modifier {
  id: string;
  name: string;
  price: number;
}
export function ProductEdit() {
  const navigate = useNavigate();
  const {
    productId
  } = useParams<{
    productId: string;
  }>();
  const [params] = useSearchParams();
  const shopId = params.get('shop') || '';
  const isNew = productId === 'new';
  const [name, setName] = useState(isNew ? '' : 'Swim Shorts');
  const [description, setDescription] = useState(isNew ? '' : 'Quick-dry swim shorts for men, made from recycled polyester.');
  const [sku, setSku] = useState(isNew ? '' : 'SS-003');
  const [category, setCategory] = useState(isNew ? 'Swimwear' : 'Swimwear');
  const [price, setPrice] = useState(isNew ? '0.00' : '34.99');
  const [salePrice, setSalePrice] = useState(isNew ? '' : '29.99');
  const [stockQuantity, setStockQuantity] = useState(isNew ? '0' : '28');
  const [imageUrl, setImageUrl] = useState(isNew ? '' : 'https://images.unsplash.com/photo-1591047139829-d91aecb6caea?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80');
  const [isActive, setIsActive] = useState(true);
  const [options, setOptions] = useState<Option[]>(isNew ? [] : [{
    id: 'o1',
    name: 'Size',
    values: 'S, M, L, XL'
  }, {
    id: 'o2',
    name: 'Color',
    values: 'Navy, Coral, Black'
  }]);
  const [modifiers, setModifiers] = useState<Modifier[]>(isNew ? [] : [{
    id: 'm1',
    name: 'Express delivery',
    price: 5.0
  }]);
  const [savedToast, setSavedToast] = useState(false);
  const addOption = () => setOptions([...options, {
    id: 'o' + Math.random().toString(36).slice(2, 7),
    name: '',
    values: ''
  }]);
  const removeOption = (id: string) => setOptions(options.filter(o => o.id !== id));
  const addModifier = () => setModifiers([...modifiers, {
    id: 'm' + Math.random().toString(36).slice(2, 7),
    name: '',
    price: 0
  }]);
  const removeModifier = (id: string) => setModifiers(modifiers.filter(m => m.id !== id));
  const handleSave = () => {
    setSavedToast(true);
    setTimeout(() => {
      setSavedToast(false);
      navigate(shopId ? `/shop-details?id=${shopId}` : '/shops');
    }, 1200);
  };
  return <div className="max-w-[1200px] mx-auto space-y-6" data-id="element-12466" data-mp="3oq167sdjxz4y">
      <div data-id="element-12467" data-mp="3oqt08yri3ot4">
        <button onClick={() => navigate(shopId ? `/shop-details?id=${shopId}` : '/shops')} className="inline-flex items-center text-sm text-gray-500 hover:text-gray-900 mb-3" data-id="element-12468" data-mp="3osdl1wo0jtds">
          <ArrowLeftIcon size={14} className="mr-1.5" data-id="element-12469" data-mp="37gp6k6pb70l9" />
          Back to shop
        </button>
        <div className="flex items-center justify-between" data-id="element-12470" data-mp="33fdfrxup89bh">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-12471" data-mp="33gy59925p22h">
            {isNew ? 'New product' : 'Edit product'}
          </h1>
          <div className="flex items-center gap-2" data-id="element-12472" data-mp="3cg1r2kspjvko">
            <button onClick={() => navigate(-1)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50" data-id="element-12473" data-mp="3cf9xffio87x1">
              Cancel
            </button>
            <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm" data-id="element-12474" data-mp="409mhybkbfwj">
              Save product
            </button>
          </div>
        </div>
      </div>

      {savedToast && <div className="fixed bottom-6 right-6 bg-gray-900 text-white px-4 py-2.5 rounded-lg text-sm shadow-lg flex items-center gap-2 z-50" data-id="element-12475" data-mp="20sjp3aoxpvob">
          <CheckCircleIcon size={16} className="text-green-400" data-id="element-12476" data-mp="236ezjamsxgcw" />
          Product saved
        </div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-12477" data-mp="2777bwcqz01de">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6" data-id="element-12478" data-mp="2c96ehkevyu9a">
          {/* Basic info */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12479" data-mp="2h77utxkxgfh2">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-12480" data-mp="1jjposuinhrgr">
              <h2 className="text-sm font-semibold text-gray-900" data-id="element-12481" data-mp="1jhd0lzmaukv9">
                Basic information
              </h2>
            </div>
            <div className="px-6 py-5 space-y-4" data-id="element-12482" data-mp="1th3txxz4ittr">
              <div data-id="element-12483" data-mp="1tfanojsfabma">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12484" data-mp="1y83rn5g2hfxb">
                  Name <span className="text-red-400" data-id="element-12485" data-mp="10t8154codm29">*</span>
                </label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12486" data-mp="15rt2x87ksdst" />
              </div>
              <div data-id="element-12487" data-mp="o9b32w3exim5">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12488" data-mp="o8svj3bavhyx">
                  Description
                </label>
                <textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12489" data-mp="f95wup9ygq9k" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-12490" data-mp="m2wmau903ij">
                <div data-id="element-12491" data-mp="3rr2pzw2ozcdh">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12492" data-mp="3rpjai7ov15cr">
                    SKU
                  </label>
                  <input type="text" value={sku} onChange={e => setSku(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12493" data-mp="3d3m67fkamnw4" />
                </div>
                <div data-id="element-12494" data-mp="39328oavt5i9g">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12495" data-mp="394mu9b059qv6">
                    Category
                  </label>
                  <input type="text" value={category} onChange={e => setCategory(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12496" data-mp="4n0tposgdco9" />
                </div>
              </div>
            </div>
          </div>

          {/* Pricing & inventory */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12497" data-mp="oe4ispu74vy2">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-12498" data-mp="xp0hfignm6cw">
              <h2 className="text-sm font-semibold text-gray-900" data-id="element-12499" data-mp="toqdscgc3s17">
                Pricing & inventory
              </h2>
            </div>
            <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-12500" data-mp="17ir5im8vces2">
              <div data-id="element-12501" data-mp="12ilk3nt6zoif">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12502" data-mp="12k64wlppft33">
                  Regular price (MYR) <span className="text-red-400" data-id="element-12503" data-mp="ar5myzu0ejdy">*</span>
                </label>
                <input type="number" step="0.01" value={price} onChange={e => setPrice(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12504" data-mp="oqf5w79lnrlm" />
              </div>
              <div data-id="element-12505" data-mp="1v6916sv97x0b">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12506" data-mp="1v8v76o2hy4gf">
                  Sale price (MYR)
                </label>
                <input type="number" step="0.01" value={salePrice} onChange={e => setSalePrice(e.target.value)} placeholder="Optional" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12507" data-mp="3toublplz13np" />
                <p className="text-xs text-gray-400 mt-1" data-id="element-12508" data-mp="h5kyufjhftuh">
                  Leave blank for no discount
                </p>
              </div>
              <div data-id="element-12509" data-mp="q7lw3cepufw5">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12510" data-mp="q84230r6k8h9">
                  Stock quantity
                </label>
                <input type="number" value={stockQuantity} onChange={e => setStockQuantity(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12511" data-mp="2n1ypvc4z8uu8" />
              </div>
            </div>
          </div>

          {/* Product options */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12512" data-mp="25qjv4s0q5cyy">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between" data-id="element-12513" data-mp="25op7h5y2j6r6">
              <div data-id="element-12514" data-mp="33bxxne90l3zi">
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-12515" data-mp="2yfh1pxvkplbv">
                  Product options
                </h2>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-12516" data-mp="2tf31h207rfoe">
                  Variants the customer must pick from, e.g. Size or Color.
                </p>
              </div>
              <button onClick={addOption} className="inline-flex items-center gap-1.5 text-sm text-[#E2231A] hover:text-[#C41B14] font-medium" data-id="element-12517" data-mp="1i1h1elstfto3">
                <PlusIcon size={14} data-id="element-12518" data-mp="y9cwqlvrwzdj" />
                Add option
              </button>
            </div>
            <div className="px-6 py-5 space-y-3" data-id="element-12519" data-mp="u8asfqn01x4p">
              {options.map((opt, i) => <div key={opt.id} className="border border-gray-200 rounded-lg p-3 grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-3 items-end" data-id="element-12520" data-mp="17z5x0qqgts2h" data-mpi={i}>
                  <div data-id="element-12521" data-mp="1hxdc1417201u">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-12522" data-mp="1hwkcan0nkat7">
                      Option name
                    </label>
                    <input type="text" value={opt.name} onChange={e => setOptions(options.map(o => o.id === opt.id ? {
                  ...o,
                  name: e.target.value
                } : o))} placeholder="e.g. Size" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12523" data-mp="1qy1qfzui22f9" />
                  </div>
                  <div data-id="element-12524" data-mp="1ki1iur6z72za">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-12525" data-mp="1khibg4eybwzi">
                      Values (comma separated)
                    </label>
                    <input type="text" value={opt.values} onChange={e => setOptions(options.map(o => o.id === opt.id ? {
                  ...o,
                  values: e.target.value
                } : o))} placeholder="e.g. S, M, L, XL" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12526" data-mp="1bhmutk2mn9s3" />
                  </div>
                  <button onClick={() => removeOption(opt.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md" aria-label="Remove option" data-id="element-12527" data-mp="15es1l3rhhlbo">
                    <Trash2Icon size={16} data-id="element-12528" data-mp="wlexd5nyyxaa" />
                  </button>
                </div>)}
              {options.length === 0 && <p className="text-sm text-gray-500 text-center py-3" data-id="element-12529" data-mp="1p30vbi8odzda">
                  No options yet — add one to let customers pick a variant.
                </p>}
            </div>
          </div>

          {/* Modifiers */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12530" data-mp="1roqo44x0h978">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between" data-id="element-12531" data-mp="1wnl6j5r2gvqt">
              <div data-id="element-12532" data-mp="21kjxxgjciolb">
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-12533" data-mp="21k1v2126hc4f">
                  Modifiers
                </h2>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-12534" data-mp="25l3w8nfnny91">
                  Optional add-ons with their own price (e.g. gift wrap,
                  monogram).
                </p>
              </div>
              <button onClick={addModifier} className="inline-flex items-center gap-1.5 text-sm text-[#E2231A] hover:text-[#C41B14] font-medium" data-id="element-12535" data-mp="2fjb84rv37q46">
                <PlusIcon size={14} data-id="element-12536" data-mp="2pda7jj3s0vsf" />
                Add modifier
              </button>
            </div>
            <div className="px-6 py-5 space-y-3" data-id="element-12537" data-mp="2tapw7d3uqhi3">
              {modifiers.map(m => <div key={m.id} className="border border-gray-200 rounded-lg p-3 grid grid-cols-1 sm:grid-cols-[2fr_1fr_auto] gap-3 items-end" data-id="element-12538" data-mp="2y9sywqwd0bmw">
                  <div data-id="element-12539" data-mp="2glh35zj1r9xg">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-12540" data-mp="2ueykddo9gakb">
                      Modifier name
                    </label>
                    <input type="text" value={m.name} onChange={e => setModifiers(modifiers.map(x => x.id === m.id ? {
                  ...x,
                  name: e.target.value
                } : x))} placeholder="e.g. Gift wrap" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12541" data-mp="2phygn9evvjwd" />
                  </div>
                  <div data-id="element-12542" data-mp="18x5zi63ac4sr">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-12543" data-mp="18ti8bdlbbuh7">
                      Extra price (MYR)
                    </label>
                    <input type="number" step="0.01" value={m.price} onChange={e => setModifiers(modifiers.map(x => x.id === m.id ? {
                  ...x,
                  price: Number(e.target.value)
                } : x))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12544" data-mp="zvg64upuevsc" />
                  </div>
                  <button onClick={() => removeModifier(m.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md" aria-label="Remove modifier" data-id="element-12545" data-mp="2g7s44v132nk3">
                    <Trash2Icon size={16} data-id="element-12546" data-mp="2277no5in53tr" />
                  </button>
                </div>)}
              {modifiers.length === 0 && <p className="text-sm text-gray-500 text-center py-3" data-id="element-12547" data-mp="1xf6amhdnf92o">
                  No modifiers yet — add one to offer paid extras.
                </p>}
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-6" data-id="element-12548" data-mp="1ij8lvk62170h">
          {/* Image */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12549" data-mp="1iip6ob7sgwq5">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-12550" data-mp="1eiev8j189e3w">
              <ImageIcon size={16} className="text-gray-500" data-id="element-12551" data-mp="3q4gvf5acgk3y" />
              <h2 className="text-sm font-semibold text-gray-900" data-id="element-12552" data-mp="3u6apuvty4rvo">
                Product image
              </h2>
            </div>
            <div className="px-6 py-5 space-y-3" data-id="element-12553" data-mp="31u5g1t6cx2m">
              <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border border-gray-200" data-id="element-12554" data-mp="7zm033l7exai">
                {imageUrl ? <img src={imageUrl} alt="Preview" className="w-full h-full object-cover" data-id="element-12555" data-mp="36ikgiv3m60b6" /> : <div className="w-full h-full flex items-center justify-center text-gray-400" data-id="element-12556" data-mp="3gee191rhl041">
                    <ImageIcon size={32} data-id="element-12557" data-mp="3l775loj1m6fl" />
                  </div>}
              </div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12558" data-mp="2npqef1r3a2od">
                Image URL
              </label>
              <input type="url" value={imageUrl} onChange={e => setImageUrl(e.target.value)} placeholder="https://..." className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12559" data-mp="1m2ipql7ybom9" />
            </div>
          </div>

          {/* Visibility */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12560" data-mp="2514qfyzk3dpn">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-12561" data-mp="202bck5gcxkoy">
              <h2 className="text-sm font-semibold text-gray-900" data-id="element-12562" data-mp="201s2fawy80lh">
                Visibility
              </h2>
            </div>
            <div className="px-6 py-5" data-id="element-12563" data-mp="29s393580onw2">
              <label className="flex items-center cursor-pointer" data-id="element-12564" data-mp="29t5y17ls4g9c">
                <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="sr-only" data-id="element-12565" data-mp="2nrmh7y0tvz8d" />
                <div className={`relative w-9 h-5 rounded-full transition-colors ${isActive ? 'bg-[#E2231A]' : 'bg-gray-200'}`} data-id="element-12566" data-mp="32e1jpsbmb4zk">
                  <div className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${isActive ? 'translate-x-4' : ''}`} data-id="element-12567" data-mp="37bbja0sq0x14" />
                </div>
                <span className="ml-3 text-sm text-gray-700" data-id="element-12568" data-mp="2jo3hvtyey84d">
                  {isActive ? 'Active — visible in shop' : 'Inactive — hidden from shop'}
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>;
}